import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { 
  HomeIcon, 
  CubeIcon, 
  MapPinIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';

const Sidebar = () => {
  const { state, logout } = useApp();
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState({ packages: false });

  const handleLogout = () => {
    logout();
  };

  const toggleMenu = (menuName) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuName]: !prev[menuName]
    }));
  };

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
    { 
      name: 'Packages', 
      href: '/packages', 
      icon: CubeIcon,
      hasSubmenu: true,
      submenu: [
        { name: 'All Bookings', href: '/all-bookings', icon: UserIcon },
        { name: 'Holiday Packages', href: '/holiday-packages', icon: MapPinIcon }
      ]
    },
    { name: 'Contact Us', href: '/contact-us', icon: ChatBubbleLeftRightIcon },
    { name: 'Change Password', href: '/change-password', icon: UserIcon },
  ];

  const isActive = (path) => {
    return location.pathname === path;
  };

  const isSubmenuActive = (submenu) => {
    return submenu.some(item => isActive(item.href));
  };

  return (
    <div className="flex flex-col h-full bg-gray-900 text-white">
      {/* Logo */}
      <div className="flex flex-col items-center px-6 py-4 border-b border-gray-700">
        <img 
          src={`${process.env.PUBLIC_URL}/sparks_logo_file.svg`} 
          alt="Sparks Logo" 
          className="h-8 w-8 mb-2"
        />
        <h1 className="text-xl font-bold text-white">Sparks Vendor</h1>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 space-y-2">
        {navigation.map((item) => {
          const Icon = item.icon;
          const isExpanded = expandedMenus[item.name.toLowerCase().replace(' ', '')];
          const hasActiveSubmenu = item.hasSubmenu && isSubmenuActive(item.submenu);
          
          return (
            <div key={item.name}>
              {item.hasSubmenu ? (
                <div>
                  <button
                    onClick={() => toggleMenu(item.name.toLowerCase().replace(' ', ''))}
                    className={`flex items-center justify-between w-full px-4 py-3 text-sm font-medium rounded-lg transition-colors duration-200 ${
                      isActive(item.href) || hasActiveSubmenu
                        ? 'bg-primary-600 text-white'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    }`}
                  >
                    <div className="flex items-center">
                      <Icon className="h-5 w-5 mr-3" />
                      {item.name}
                    </div>
                    {isExpanded ? (
                      <ChevronDownIcon className="h-4 w-4" />
                    ) : (
                      <ChevronRightIcon className="h-4 w-4" />
                    )}
                  </button>
                  
                  {isExpanded && (
                    <div className="ml-4 mt-2 space-y-1">
                      {item.submenu.map((subItem) => {
                        const SubIcon = subItem.icon;
                        return (
                          <Link
                            key={subItem.name}
                            to={subItem.href}
                            className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                              isActive(subItem.href)
                                ? 'bg-primary-500 text-white'
                                : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                            }`}
                          >
                            <SubIcon className="h-4 w-4 mr-3" />
                            {subItem.name}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to={item.href}
                  className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors duration-200 ${
                    isActive(item.href)
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  <Icon className="h-5 w-5 mr-3" />
                  {item.name}
                </Link>
              )}
            </div>
          );
        })}
      </nav>

      {/* User Info & Logout */}
      <div className="px-4 py-4 border-t border-gray-700">
        <div className="flex items-center mb-4">
          <div className="flex-shrink-0">
            <UserIcon className="h-8 w-8 text-gray-400" />
          </div>
          <div className="ml-3">
            <div className="text-sm font-medium text-white">
              {state.user?.name}
            </div>
            <div className="text-xs text-gray-400">
              {state.user?.email}
            </div>
            <div className="text-xs text-gray-400">
              {state.user?.phone}
            </div>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white rounded-lg transition-colors duration-200"
        >
          <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3" />
          Logout
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
